/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import LeftSidebarPrototype from './chat/components/LeftSidebarPrototype';
import styles from './SidebarPrototypePage.module.scss';
import { animated, useSpring } from 'react-spring';
import Navbar from '../components/navbar/Navbar';
import {
  otherTemplateTypes,
  paths,
  qualityTemplateTypes,
} from '../constants/strings';
import { useReactiveVar } from '@apollo/client';
import { navigationExpandedVar } from '../graphql/cache/navbar';
import { useIsMobile } from '../hooks/misc';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { actions, customColors, sortAlphabeticalOrder } from '../utilities';
import { useCurrentUser } from '../providers/UserProvider';
import { useTranslation } from 'react-i18next';
import { keys } from '../utilities/translator/translation_keys';
import { useWorkspace } from '../providers/WorkspaceProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import useCheckPermission from '../providers/UsePermissionHook';
import { useTemplates } from '../providers/TemplateProvider';

export default function SidebarPrototypePage({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useIsMobile();
  const { workspace } = useWorkspace();
  const navigationExpanded = useReactiveVar(navigationExpandedVar);
  const { user, isAdmin, assessmentTypes } = useCurrentUser();
  const { activeTemplateTypes } = useTemplates();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const online = useOnlineStatus();
  const blurOfflineComponents =
    !online &&
    !pathname.includes('assessment') & !pathname.includes('dashboard');

  const onlineStatus = useOnlineStatus();

  useEffect(() => {
    setCollapsed(false);
  }, [navigationExpanded]);

  const { marginLeft } = useSpring({
    config: { duration: 500 },
    width: collapsed ? '4.375rem' : '14rem',
    marginLeft: collapsed ? '4.375rem' : '13rem',
  });

  const bottomOptions = [
    {
      icon: 'settings',
      title: t(keys.settings.SETTINGS),
      path: `${paths.settings}?tab=${isAdmin ? 'settings' : 'notifications'}`,
      param: 'settings',
    },
    {
      icon: 'logout',
      title: t(keys.utilities.SIGNOUT),
      onClick: async () => {
        navigate(paths.signout);
      },
    },
  ];

  const hasInvitePermission = useCheckPermission({
    model: 'user',
    action: actions.invite,
  });

  const hasInsightPermission = useCheckPermission({
    model: 'report',
    action: actions.view,
  });

  const hasRiskPermission = useCheckPermission({
    model: 'riskRegistry',
    action: actions.review,
  });

  const hasAnnouncementManagePermission = useCheckPermission({
    model: 'announcement',
    action: actions.create,
  });

  const hasManageTasksPermission = useCheckPermission({
    model: 'task',
    action: actions.create,
  });

  const {
    enableTimecards,
    enableExpenses,
    auditsAndCorrectiveActions,
    disableIncidents,
    disableObservations,
    enableLoneWorker,
    enableQualityModule,
    enableCertifications,
    enableCustomToolboxTalks,
    disableTasks,
    disableAnnouncements,
    enableCustomReports,
    enableHazardLog,
  } = useFlags();

  const hasAuditPermission = useCheckPermission({
    model: 'audit',
    action: actions.create,
  });

  const hasQualityPermission = useCheckPermission({
    model: 'quality',
    action: actions.view,
  });

  const hasPermissionsManagerAccess = useCheckPermission({
    model: 'permissionManager',
    action: actions.view,
  });

  const assessmentOptions =
    Object.values(assessmentTypes).filter(
      (a) =>
        activeTemplateTypes.includes(a.key) &&
        !otherTemplateTypes.includes(a.key) &&
        !a.sidebarSection,
    ) || [];

  const customSidebarItemsBySection = useMemo(() => {
    const map = {};
    Object.values(assessmentTypes)
      .filter((a) => a.sidebarSection && a.active)
      .forEach((a) => {
        const item = {
          icon: a.icon,
          title: a.name,
          path: `/workspace/${workspace?.id}/assessments?tab=assessments&inspectionType=${a.key}&lockedType=${a.key}`,
          param: 'assessments',
          lockedType: a.key,
          show: true,
        };
        if (!map[a.sidebarSection]) map[a.sidebarSection] = [];
        map[a.sidebarSection].push(item);
      });
    return map;
  }, [assessmentTypes, workspace?.id]);

  const actionItemTypes =
    [
      ...Object.values(assessmentTypes).filter(
        (a) => activeTemplateTypes.includes(a.key) && a.key !== 'AUDIT',
      ),
      {
        title: 'General',
        icon: 'task_alt',
        color: customColors.BLUE_LIGHT,
        name: 'General',
        key: 'GENERAL',
      },
    ] || [];

  const nonQuality = actionItemTypes?.filter(
    (a) => !qualityTemplateTypes.includes(a.key),
  );
  const cpaOption = {
    title: 'Corrective',
    icon: 'error_outline',
    color: customColors.RED_LIGHT,
    tab: 'cpa',
    param: 'cpa',
    key: 'CPA',
    urlParam: 'CPA',
  };
  const KEY_SORT_ALIASES = {
    vehicle: 'fleet',
  };

  const collator = new Intl.Collator(undefined, {
    sensitivity: 'base',
    numeric: true,
  });

  const getSortKey = (opt) =>
    KEY_SORT_ALIASES[opt.key?.toLowerCase()] ?? opt.key ?? '';

  const actionItemOpts = useMemo(() => {
    const options = enableQualityModule
      ? [...actionItemTypes]
      : [...nonQuality];
    if (auditsAndCorrectiveActions) options.push(cpaOption);

    options.sort((a, b) => collator.compare(getSortKey(a), getSortKey(b)));
    return options;
  }, [
    enableQualityModule,
    auditsAndCorrectiveActions,
    actionItemTypes,
    nonQuality,
    cpaOption,
  ]);

  const dashboardOption = {
    icon: onlineStatus ? 'dashboard' : 'wifi_off',
    title: t(keys.dashboard.DASHBOARD),
    path: `/workspace/${workspace?.id}/dashboard`,
    param: 'dashboard',
  };

  const actionItemOptions =
    sortAlphabeticalOrder(actionItemOpts, 'key') || actionItemOpts || [];

  const options = useMemo(() => {
    return {
      REPORTING: [
        {
          icon: 'task_alt',
          title: t(keys.common.ACTION_ITEMS),
          path: `/workspace/${workspace?.id}/actionitems?tab=open`,
          param: 'actionitems',
          show: true,
          children: actionItemOptions,
          urlParam: 'type',
        },
        {
          icon: 'inventory',
          title: user?.company?.assessmentName || t(keys.common.ASSESSMENTS),
          path: `/workspace/${workspace?.id}/assessments?tab=assessments`,
          param: 'assessments',
          show: true,
          children: assessmentOptions,
          urlParam: 'inspectionType',
        },
      ],
      HSE: [
        {
          icon: 'content_paste_search',
          title: user.company?.observationName || t(keys.common.OBSERVATIONS),
          path: `/${paths.observations}?tab=observations`,
          param: paths.observations,
          show: !disableObservations,
        },
        {
          icon: 'fmd_bad',
          title: user.company?.incidentName || t(keys.incidents.INCIDENTS),
          path: `/${paths.incidents}?tab=incidents`,
          param: 'incidents',
          show: !disableIncidents,
        },
        {
          icon: 'card_membership',
          title: 'Certifications',
          path: `${paths.certifications}?tab=requirements`,
          param: 'certifications',
          show: enableCertifications,
        },
        {
          icon: 'accessibility_new',
          title: user.company?.checkInName || 'Check Ins',
          path: `/checkins`,
          param: 'checkins',
          show: enableLoneWorker,
        },
        {
          icon: 'health_and_safety',
          title: user.company?.riskRegistryName || 'Risk Registry',
          path: `/registry`,
          param: paths.registry,
          show: enableHazardLog,
        },
        ...(customSidebarItemsBySection['HSE'] || []),
      ],
      QC: [
        {
          icon: 'auto_awesome',
          title: 'Quality',
          path: `/workspace/${workspace?.id}/quality?tab=quality`,
          param: 'quality',
          show: enableQualityModule && hasQualityPermission,
        },
        {
          icon: 'checklist',
          title: user.company?.auditName || 'Audits',
          path: `/audits?tab=audits`,
          param: 'audits',
          show: auditsAndCorrectiveActions && hasAuditPermission,
        },
        {
          icon: 'error_outline',
          title: 'Corrective Actions',
          path: `/audits?tab=cpas`,
          param: 'cpas',
          tab: 'cpas',
          show: auditsAndCorrectiveActions && hasAuditPermission,
        },
        ...(customSidebarItemsBySection['QC'] || []),
      ],
      HR: [
        {
          icon: 'paid',
          title: t(keys.common.EXPENSES),
          path: `/${paths.expenses}?tab=personal`,
          param: 'expenses',
          show: enableExpenses,
        },
        {
          icon: 'timer',
          title: t(keys.common.TIMECARDS),
          path: `/${paths.timecards}?tab=personal`,
          param: 'timecards',
          show: enableTimecards,
        },
        {
          icon: 'person',
          title: t(keys.common.USERS),
          path: `/${paths.users}`,
          param: 'users',
          show: hasInvitePermission,
        },
        ...(customSidebarItemsBySection['HR'] || []),
      ],
      UTILITIES: [
        {
          icon: 'article',
          title: t(keys.common.TEMPLATES),
          path: `/templates`,
          param: 'templates',
          show: isAdmin,
        },
        {
          icon: 'local_shipping',
          title: t(keys.common.ASSETS),
          path: `/workspace/${workspace?.id}/assets?tab=assets`,
          param: 'assets',
          show: true,
        },
        {
          icon: 'folder',
          title: user?.company?.filesName || t(keys.common.FILES),
          path: `/workspace/${workspace?.id}/${paths.files}`,
          param: 'files',
          show: true,
        },
        {
          icon: 'assignment_turned_in',
          title: t(keys.common.TASKS),
          path: `/${paths.tasks}?tab=INSPECTION`,
          param: 'tasks',
          show: hasManageTasksPermission && !disableTasks,
        },
        {
          icon: 'home_repair_service',
          title: user.company?.toolboxName || t(keys.toolbox.TOOLBOX),
          path: '/toolbox?tab=schedule',
          param: 'toolbox',
          show: enableCustomToolboxTalks,
        },
        {
          icon: 'campaign',
          title: t(keys.common.ANNOUNCEMENTS),
          path: `/${paths.announcements}`,
          param: 'announcements',
          show: !disableAnnouncements && hasAnnouncementManagePermission,
        },
        ...(customSidebarItemsBySection['UTILITIES'] || []),
      ],
      Admin: [
        {
          icon: 'insights',
          title: t(keys.common.INSIGHTS),
          path: `${paths.analytics}/?charttab=ASSESSMENTS`,
          param: paths.analytics,
          show: hasInsightPermission,
        },
        {
          icon: 'add_chart',
          title: 'Analytics',
          path: `/reports`,
          param: 'reports',
          show: hasInsightPermission && enableCustomReports,
        },
        {
          icon: 'admin_panel_settings',
          title: 'Permissions',
          path: '/permissions',
          param: 'permissions',
          show: hasPermissionsManagerAccess,
        },
        ...(customSidebarItemsBySection['Admin'] || []),
      ],
    };
  }, [
    i18n?.language,
    t,
    workspace?.id,
    user,
    actionItemOptions,
    assessmentOptions,
    disableObservations,
    disableIncidents,
    enableCertifications,
    enableLoneWorker,
    enableQualityModule,
    isAdmin,
    auditsAndCorrectiveActions,
    hasAuditPermission,
    enableExpenses,
    enableTimecards,
    hasInvitePermission,
    hasManageTasksPermission,
    disableTasks,
    enableCustomToolboxTalks,
    disableAnnouncements,
    hasAnnouncementManagePermission,
    hasInsightPermission,
    enableCustomReports,
    enableHazardLog,
    hasRiskPermission,
    hasPermissionsManagerAccess,
    customSidebarItemsBySection,
  ]);

  return (
    <div className={styles.background}>
      <LeftSidebarPrototype
        options={onlineStatus ? options : []}
        bottomOptions={bottomOptions}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        dashboardOption={dashboardOption}
      />
      <div>
        <animated.div style={{ marginLeft: isMobile ? 0 : marginLeft }}>
          <Navbar className={styles.navbar} left={marginLeft} />
          <div
            className={styles.page}
            inert={blurOfflineComponents ? '' : undefined}
            aria-hidden={blurOfflineComponents ? true : undefined}
            style={{ opacity: blurOfflineComponents ? 0.5 : 1 }}
          >
            {children}
          </div>
        </animated.div>
      </div>
    </div>
  );
}
